import { useNavigate } from "@tanstack/react-router"
import { createFileRoute } from "@tanstack/react-router"
import { useCallback, useEffect } from "react"

import { finishAndGetNextStep, useOrderedSteps, useSessionDetail } from "@/services/dexie"

import { ClientRedirect } from "../../../../components/ClientRedirect"
import { SpinnerLayout } from "../../../../components/layouts/SpinnerLayout"
import { getStepRoute, LINKS } from "../../../../routing/links"
import { useIntroSubSteps } from "../../../../sections/intro/useIntroSubSteps"

export const Route = createFileRoute("/$sessionId/_session/intro/")({
  component: IntroIndexRedirect,
})

function IntroIndexRedirect() {
  const { sessionId } = Route.useParams()
  const { data: detail, isLoading: detailLoading } = useSessionDetail(sessionId)

  const introSubSteps = useIntroSubSteps(detail)
  const { data: orderedSteps } = useOrderedSteps(sessionId)
  const introOrderedStep = orderedSteps?.find((s) => s.name === "Intro")

  // Still loading from Dexie
  if (detailLoading || !detail || !orderedSteps || orderedSteps.length === 0) return <SpinnerLayout />

  // Intro already finished — redirect to next unfinished step
  if (introOrderedStep?.isFinished) {
    const nextStep = orderedSteps.find((s) => !s.isFinished)
    if (!nextStep) return <ClientRedirect to={LINKS.finalCheck(sessionId)} />

    return <ClientRedirect to={getStepRoute(nextStep, sessionId)} />
  }

  if (introSubSteps.length === 0) {
    // Intro is disabled with no sub-steps — mark as done and skip
    const nextStepAfterIntro = orderedSteps.find((s) => s.name !== "Intro" && !s.isFinished)
    const redirectTo = nextStepAfterIntro ? getStepRoute(nextStepAfterIntro, sessionId) : LINKS.finalCheck(sessionId)

    return <FinishIntroAndRedirect sessionId={sessionId} redirectTo={redirectTo} />
  }

  const firstSubStep = introSubSteps[0]
  return <ClientRedirect to={`${LINKS.intro(sessionId)}/${firstSubStep.id}`} />
}

function FinishIntroAndRedirect({ sessionId, redirectTo }: { sessionId: string; redirectTo: string }) {
  const navigate = useNavigate()

  const finishAndNavigate = useCallback(async () => {
    await finishAndGetNextStep("Intro", sessionId)
    await navigate({ to: redirectTo, replace: true })
  }, [navigate, redirectTo, sessionId])

  useEffect(() => {
    finishAndNavigate()
  }, [sessionId, redirectTo, navigate, finishAndNavigate])

  return null
}
